import { Navigate } from 'react-router-dom';

import BillingPage from './features/billing';
import AIInsightsLayout from './pages/AIInsightsLayout';
import DiagnosticsView, { DiscoverabilityView } from './features/ai-insights';
import DashboardV3Page from './pages/DashboardPage';
import DiagnosticsReportPage from './pages/DiagnosticsReportPage';
import Domain404CheckerPage from './pages/404CheckerPage';
import IntegrationDetailsPage from './features/integration/IntegrationDetailsPage';
import IntegrationPage from './features/integration/IntegrationPage';
import IntegrationVerificationPage from './features/integration/IntegrationVerificationPage';
import RegistrationPage from './features/registration/RegistrationPage';
import SitemapPage from './pages/SitemapPage';
import NewCacheManagerPage from './pages/NewCacheManagerPage';
import CDNAnalyticsPage from './pages/CDNAnalyticsPage';
import CustomDealTrackerPage from './pages/CustomDealTrackerPage';
import Error404Page from './pages/Error404Page';
import InsightPage from './pages/InsightPage';
import QualityAssurancePage from './pages/QualityAssurancePage';
import RawHtmlPage from './pages/RawHtmlPage';
import RedirectPage from './pages/RedirectPage';
import SettingsPage from './pages/SettingsPage';
import SignOutPage from './pages/SignOutPage';
import UncaughtError from './pages/UncaughtError';
import NewlyAddedDomainsPage from './features/added-domains/NewlyAddedDomainsPage';
import DomainsCountPage from './features/domains-count/DomainsCountPage';
import DomainManagerPage from './pages/DomainsManager';
import SecurityAndAccess from './pages/SecurityAndAccess';
import SeoReportPage from './features/SeoReport/SeoReportPage';
import OversightPage from './features/oversightSystem/OversightPage';
import DemoSitemapPage from './pages/demo/DemoSitemapPage';
import { DevTestPage } from './pages/DevTestPage';
import AnalyticsPage from './features/Analytics/AnalyticsPage';
import DetailedView from './features/domain-manager/DetailedView';
import NotificationsPage from './pages/NotificationsPage';
import IntegrationsLayout, { GscSettingsRoute } from './pages/IntegrationsLayout';
import Integrations from './features/Integrations';
import SearchPerformancePage from './pages/SearchPerformancePage';
import SearchPerformanceConnectPage from './pages/SearchPerformanceConnectPage';
import GscOAuthCallbackPage from './pages/GscOAuthCallbackPage';
import MyAccountPage from './features/my-account/MyAccountPage';
import NewScheduledRenderingPage from './pages/NewScheduledRenderingPage';
import NewRenderHistoryPage from './pages/NewRenderHistoryPage';
import IntegrationWizardPage from './pages/IntegrationWizardPage';
import OnboardingSurveyPage from './pages/OnboardingSurveyPage';
import DashboardExport from './pages/DashboardExport';
import CDNAnalyticsExport from './pages/CDNAnalyticsExport';
import DiagnosticsReportExport from './pages/DiagnosticsReportExport';
import AIInsightsDiscoverabilityExport from './pages/AIInsightsDiscoverabilityExport';

const routes = [
  { element: <RedirectPage />, path: '/login' },
  { element: <RedirectPage />, path: '/reset-password' },
  { element: <RedirectPage />, path: '/signup' },
  { element: <RedirectPage />, path: '/verify-email' },
  { element: <RedirectPage />, path: '/accept-invitation' },
  { element: <DashboardV3Page />, path: '/' },
  { element: <DashboardExport />, path: '/export-dashboard' },
  { element: <CDNAnalyticsExport />, path: '/export-cdn-analytics' },
  {
    element: <AIInsightsDiscoverabilityExport />,
    path: '/export-ai-insights-discoverability',
  },
  { element: <DiagnosticsReportExport />, path: '/ai-insights/diagnostics/report/:reportId/export' },
  { element: <DiagnosticsReportPage />, path: '/ai-insights/diagnostics/report/:reportId' },
  {
    path: '/ai-insights',
    element: <AIInsightsLayout />,
    children: [
      { index: true, element: <Navigate to="/ai-insights/discoverability" replace /> },
      { path: 'discoverability', element: <DiscoverabilityView /> },
      { path: 'diagnostics', element: <DiagnosticsView /> },
    ],
  },
  { element: <NewCacheManagerPage />, path: '/cache/*', event: 'Cache Manager Viewed' },
  { element: <NewScheduledRenderingPage />, path: '/recache-queue/*', event: 'Render Queue Viewed' },
  { element: <NewRenderHistoryPage />, path: '/recache-stats', event: 'Render History Viewed' },
  { element: <CDNAnalyticsPage />, path: '/cdn-analytics' },
  { element: <SettingsPage />, path: '/settings/*', event: 'Settings Viewed' },
  { element: <SecurityAndAccess />, path: '/security/*', event: 'Security and Access Viewed' },
  { element: <IntegrationPage />, path: '/integration' },
  { element: <IntegrationDetailsPage />, path: '/integration/:name' },
  { element: <IntegrationVerificationPage />, path: '/integration/:name/verification' },
  { element: <SignOutPage />, path: '/signout' }, // this is used by keycloak too
  { element: <RawHtmlPage />, path: '/raw-html' },
  { element: <BillingPage />, path: '/billing/*', event: 'Billing Page Viewed' },
  { element: <Error404Page />, path: '/error404' },
  { element: <UncaughtError />, path: '/error-uncaught' },
  // { element: <RegistrationPage />, path: '/registration' },
  {
    element: <OnboardingSurveyPage />,
    path: '/onboarding-survey',
  },
  { element: <Navigate to="/integrations" replace />, path: '/gsc' }, // superseded by the Integrations page
  {
    element: <IntegrationWizardPage />,
    path: '/integration-wizard/*',
  },
  { element: <DomainManagerPage />, path: '/domain-manager/*', event: 'Domain Manager Viewed' },
  { element: <SeoReportPage />, path: '/seo-report/:id', event: 'SEO Report Viewed' },
  { element: <SitemapPage />, path: '/sitemaps/*', event: 'Sitemap Viewed' },
  { element: <Domain404CheckerPage />, path: '/domain-404-checker', event: '404 Checker Page Viewed' },
  { element: <InsightPage />, path: '/insight', event: 'Deep Insight Viewed' },
  { element: <OversightPage />, path: '/oversight/*', event: 'Oversight Page Viewed' },
  { element: <CustomDealTrackerPage />, path: '/custom-deal' },
  { element: <NewlyAddedDomainsPage />, path: '/added-domains' },
  { element: <QualityAssurancePage />, path: '/qa' },
  { element: <DomainsCountPage />, path: '/page-count' },
  // { element: <AnalyticsPage />, path: '/analytics/:domain?', event: 'Analytics Page Viewed' }, // deactivated since its not in use and API will be reworked
  { element: <DetailedView />, path: '/detailed-view', event: 'Detailed View Page Viewed' },
  { element: <NotificationsPage />, path: '/notifications/*', event: 'Notifications Center opened' },
  { element: <MyAccountPage />, path: '/account', event: 'My Account Page Viewed' },
  // The children get no ViewEventListener event of their own (it reads only
  // top-level routes), so GscSettings tracks its view itself.
  {
    path: '/integrations',
    element: <IntegrationsLayout />,
    event: 'Integrations Page Viewed',
    children: [
      { index: true, element: <Integrations /> },
      { path: 'settings', element: <GscSettingsRoute /> },
    ],
  },
  { element: <SearchPerformancePage />, path: '/search-performance', event: 'Search Performance Viewed' },
  // Its own event: 'Search Performance Viewed' used to cover the connection
  // steps too, and ViewEventListener matches the pathname exactly, so after the
  // split that metric means dashboard views only.
  {
    element: <SearchPerformanceConnectPage />,
    path: '/search-performance/connect',
    event: 'Search Performance Connect Viewed',
  },
  { element: <GscOAuthCallbackPage />, path: '/connections/gsc/callback' },

  // REDIRECTS (OLD OR BACKWARD COMPATIBILITY)
  { element: <Navigate to="/billing/payment" replace />, path: '/account/billing' }, // redirect for backward compatibility
  { element: <Navigate to="/cdn-analytics" replace />, path: '/crawl-stats' }, // redirect for backward compatibility

  // { element: <DemoSitemapPage />, path: '/demo/sitemap' },
  // { element: <DevTestPage />, path: '/dev' },
  { element: <Error404Page />, path: '*' },
];

export default routes;
